import React from 'react';
import { Badge, Box, BoxProps, Button, Collapse, Flex, Stack, VisuallyHidden, useDisclosure } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faQuestionCircle,
  faCircleExclamationCheck,
  faCircleExclamation,
  faCirclePlus,
  faCircleXmark,
  faCircleMinus,
  faCircle,
  faCircleUser,
  faCameraViewfinder,
  faCircleInfo,
  faArrowsRotate,
} from '@fortawesome/pro-regular-svg-icons';

import { Mark, Span } from 'shared-components';

import { InfoField, InfoState, InfoOption, TransactionStatus } from 'types';

import { FingerIcon } from '../../../Finger/FingerIcon';

import { diffWordsWithSpace } from 'diff';
import { useTranslation } from "react-i18next";

interface Props {
  data: InfoField;
  onEdit: (page: string, field: InfoField) => void;
  onConflictOption: (optionId: string) => void;
}

export default function Field(props: Props) {
    const { t } = useTranslation('tables');
  const { id, label, subLabel, state, editPage, values, options, addFieldBtn, multiLine } = props.data;
  const { onEdit, onConflictOption } = props;
  const MAX_LENGTH = 80;
  const labelWidth = { base: undefined, md: '40%' };

  const {
    getDisclosureProps: getConflictDisclosureProps,
    isOpen: isConflictOpen,
    onToggle: onConflictToggle,
    getDisclosureProps: getViewMoreDisclosureProps,
    isOpen: isViewMoreOpen,
    onOpen: onViewMoreOpen,
    onClose: onViewMoreClose,
  } = useDisclosure();

  const getConflictDisclosureLabel = () => {
    const disclosureProps = getConflictDisclosureProps();

    if (disclosureProps.hidden) {
      return 'View';
    }
    return 'Hide';
  };

  const getViewMoreDisclosureLabel = () => {
    const disclosureProps = getViewMoreDisclosureProps();

    if (disclosureProps.hidden) {
      return 'more';
    }
    return 'less';
  };

  const conflictDisclosureLabel = getConflictDisclosureLabel();
  const viewMoreDisclosureLabel = getViewMoreDisclosureLabel();

  const canOpen = [InfoState.Conflict, InfoState.Update, InfoState.Resolved, InfoState.TransferMate].includes(state);

  const getStatusStyle = (state: InfoState) => {
    switch (state) {
      case InfoState.NotFilled:
        return {
          icon: faCircle,
          color: 'gray',
          tooltip: InfoState.NotFilled,
        };
      case InfoState.NotFilledImport:
        return {
          icon: faCircle,
          color: 'gray',
          tooltip: InfoState.NotFilledImport,
        };
      case InfoState.Manual:
        return {
          icon: faCircleCheck,
          color: 'green',
          tooltip: InfoState.Manual,
        };
      case InfoState.NotFilledPassport:
        return {
          icon: faCircleCheck,
          color: 'green',
          tooltip: InfoState.NotFilledPassport,
        };

      case InfoState.Filled:
        return {
          icon: faCircleUser,
          color: 'green',
          tooltip: InfoState.Filled,
        };
      case InfoState.Approved:
        return {
          icon: faCircleCheck,
          color: 'green',
          tooltip: InfoState.Approved,
        };
      case InfoState.Conflict:
        return {
          icon: faCircleExclamation,
          color: 'red',
          tooltip: InfoState.Conflict,
        };
      case InfoState.Update:
        return {
          icon: faCirclePlus,
          color: 'blue',
          tooltip: InfoState.Update,
        };
      case InfoState.Pending:
        return {
          icon: faCircle,
          color: 'cyan',
          tooltip: InfoState.Pending,
        };
      case InfoState.Resolved:
        return {
          icon: faCircleExclamationCheck,
          color: 'green',
          tooltip: InfoState.Resolved,
        };
      case InfoState.Removed:
        return {
          icon: faCircleXmark,
          color: 'gray',
          tooltip: InfoState.Removed,
        };
      case InfoState.Skipped:
        return {
          icon: faCircleMinus,
          color: 'gray',
          tooltip: InfoState.Skipped,
        };
      case InfoState.Optional:
        return {
          icon: faCircleInfo,
          color: 'gray',
          tooltip: InfoState.Optional,
        };
      case InfoState.TransferMate:
        return {
          icon: faArrowsRotate,
          color: 'green',
          tooltip: InfoState.TransferMate,
        };
      default:
        return {
          icon: faQuestionCircle,
          color: 'orange',
          tooltip: 'The system cannot determine the status of this data',
        };
    }
  };

  const { tooltip, color, icon } = getStatusStyle(state);

  const getLabel = () => {
    const subLabelNode = subLabel ? (
      <>
        {' '}
        <Box as="span" data-selector={id} fontSize="xs">
          {subLabel}
        </Box>
      </>
    ) : null;

    const getSelector = () => {
      switch (state) {
        case InfoState.Skipped:
        case InfoState.Filled:
        case InfoState.Approved:
        case InfoState.Optional:
        case InfoState.Resolved:
        case InfoState.Manual:
        case InfoState.TransferMate:
          return 'resolved';
        case InfoState.Conflict:
          return 'in-conflict';
        case InfoState.Update:
          return 'update';
        case InfoState.Pending:
          return 'pending';
        case InfoState.NotFilledPassport:
          return 'passport';
        case InfoState.Removed:
          return 'removed';
        case InfoState.NotFilled:
        case InfoState.NotFilledImport:
        default:
          return null;
      }
    };

    const selector = getSelector() ? `-${getSelector()}` : null;

    return (
      <Box as="span" data-selector={`${id}${selector}`}>
        {label}
        {subLabelNode}
      </Box>
    );
  };

  const ValueWrapper = (props: BoxProps) => {
    return (
      <Box py={{ base: 0, md: 1 }} wordBreak={'break-word'} id={`${id}-value`}>
        {props.children}
      </Box>
    );
  };

  const getValue = () => {
    switch (state) {
      case InfoState.NotFilled:
        return (
          <ValueWrapper>
            <Span color={'muted'}>{t('tables:noDataYet')}</Span>
          </ValueWrapper>
        );
      case InfoState.NotFilledImport:
      case InfoState.Skipped:
        return <ValueWrapper>{wrapValueText(values.import ?? null)}</ValueWrapper>;
      case InfoState.NotFilledPassport:
        return <ValueWrapper>{wrapValueText(values.passport ?? null)}</ValueWrapper>;
      case InfoState.Filled:
      case InfoState.Approved:
      case InfoState.Optional:
      case InfoState.Resolved:
      case InfoState.TransferMate:
        return <ValueWrapper>{wrapValueText(values.student ?? null)}</ValueWrapper>;
      case InfoState.Manual:
        return <ValueWrapper>{wrapValueText(values.import ?? null)}</ValueWrapper>;
      case InfoState.Conflict:
        return;
      case InfoState.Update:
        return;
      case InfoState.Pending:
        return (
          <ValueWrapper>
            <Badge colorScheme={color} verticalAlign={'top'}>
              {t('tables:pending')}
                                </Badge>

            {id === 'passportNumber' ? (
              <Span as={'i'} ml={2}>
                {values.student}
              </Span>
            ) : null}
          </ValueWrapper>
        );
      case InfoState.Removed:
        return <ValueWrapper>{wrapValueText(values.import ?? null)}</ValueWrapper>;
      default:
        return null;
    }
  };

  const wrapValueText = (text: string): React.ReactNode => {
    const nodes: React.ReactNode[] = [];

    if (multiLine) {
      let lines = text.split('\n');
      // guess if text is big enough for the "view more" button
      const textBigEnoughToExpand = shouldTextTruncate(lines);
      // truncate text to fit into "view more" field
      if (textBigEnoughToExpand && !isViewMoreOpen) {
        lines = getTruncatedText(lines);
      }

      nodes.push(lines.join('\n'));

      if (textBigEnoughToExpand) {
        if (isViewMoreOpen) {
          nodes.push(
            <Button variant={'ghost'} size={'sm'} key="view-less" onClick={onViewMoreClose}>
              {t('tables:view')} {viewMoreDisclosureLabel}
            </Button>,
          );
        } else {
          nodes.push('... ');
          nodes.push(
            <Button variant={'ghost'} size={'sm'} key="view-more" onClick={onViewMoreOpen}>
              {t('tables:view')} {viewMoreDisclosureLabel}
            </Button>,
          );
        }
      }
    } else {
      nodes.push(text);
    }

    return <Box whiteSpace={'pre-line'}>{nodes}</Box>;
  };

  const wrapOptionText = (option: InfoOption, isConflictHistory?: boolean): React.ReactNode[] => {
    const text = (isConflictHistory ? option.conflictHistory : option.value) ?? '';
    if (shouldHighlightText()) {
      return getHighlightedText(option.id, text);
    }
    return [text];
  };

  const shouldHighlightText = (): boolean => {
    return state === InfoState.Conflict && id === 'courseOfferEvidence';
  };

  const getHighlightedText = (optionId: string, text: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];

    const newStr = (optionId === 'import' ? values?.student : values?.import) ?? '';
    const diff = diffWordsWithSpace(newStr, text);

    diff.forEach((part: any, index) => {
      const isMark = part.added ? Mark : Span;

      if (!part.removed) {
        const textPartElement = (
          <Box as={isMark} key={`${optionId}-part-${index}`}>
            {part.value}
          </Box>
        );

        nodes.push(textPartElement);
      }
    });

    return nodes;
  };

  const shouldTextTruncate = (lines: string[]): boolean => {
    return lines.length > 2 || (lines[0]?.length ?? 0) + (lines[1]?.length ?? 0) > MAX_LENGTH * 2;
  };

  const getTruncatedText = (lines: string[]): string[] => {
    const result = [];
    if (lines[0]?.length > MAX_LENGTH) {
      result.push(lines[0].slice(0, MAX_LENGTH));
    } else {
      result.push(lines[0]);
      if (lines[1]?.length > MAX_LENGTH) {
        result.push(lines[1].slice(0, MAX_LENGTH));
      } else {
        result.push(lines[1]);
      }
    }
    return result;
  };

  const getOpenButton = () => {
    switch (state) {
      case InfoState.Resolved:
        return (
          <Button onClick={onConflictToggle} size={'sm'} variant={'ghost'} data-selector="resolved-accordion" ml={2}>
            {conflictDisclosureLabel} {t('tables:resolution')}
                          </Button>
        );
      case InfoState.Conflict:
        return (
          <Button onClick={onConflictToggle} size={'sm'} colorScheme={color} data-selector="conflict-accordion">
            {conflictDisclosureLabel} {t('tables:conflict')}
                          </Button>
        );
      case InfoState.Update:
        return (
          <Button
            onClick={onConflictToggle}
            size={'sm'}
            variant={'secondary'}
            colorScheme={color}
            data-selector="update-accordion"
          >
            {conflictDisclosureLabel} {t('tables:update')}
                          </Button>
        );
      case InfoState.TransferMate:
        return (
          <Button onClick={onConflictToggle} size={'sm'} variant={'ghost'} data-selector="details-accordion" ml={2}>
            {conflictDisclosureLabel} {t('tables:details')}
                          </Button>
        );
      default:
        return null;
    }
  };

  const getOptionValue = (option: InfoOption) => {
    const textColor = getStatusStyle(state).color;

    switch (state) {
      case InfoState.Resolved:
        return <Span>{wrapOptionText(option, true)}</Span>;
      case InfoState.TransferMate:
        return <Span>{wrapOptionText(option)}</Span>;
      case InfoState.Conflict: {
        if (option.valuePending) {
          return (
            <Span color={`${textColor}.700`} _dark={{ color: `${textColor}.300` }}>
              {wrapOptionText(option)}
            </Span>
          );
        }
        return (
          <Span color={`${textColor}.500`} _dark={{ color: `${textColor}.300` }}>
            {wrapOptionText(option)}
          </Span>
        );
      }
      case InfoState.Update:
        return (
          <Span color={`${textColor}.500`} _dark={{ color: `${textColor}.300` }}>
            {wrapOptionText(option)}
          </Span>
        );
      default:
        return null;
    }
  };

  const getOptionBtn = (option: InfoOption) => {
    switch (state) {
      case InfoState.Conflict:
        return (
          <Button size={'sm'} data-selector={`${option.id}-keep-value`} onClick={() => onConflictOption(option.id)}>
            {t('tables:keepValue')}
                          </Button>
        );
      case InfoState.Update:
        return (
          <Button size={'sm'} data-selector={`${option.id}-keep-value`} onClick={() => onConflictOption(option.id)}>
            {t('tables:keepValue')}
                          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Box pl={{ base: 0, xl: 2 }} pr={{ base: 1, xl: 2 }} py={1} borderBottomWidth={1} _last={{ borderBottomWidth: 0 }}>
      <Box
        display={{ base: 'grid', md: 'flex' }}
        gridTemplateColumns={{ base: '2rem 1fr auto', md: null }}
        gridTemplateRows={{ base: 'auto auto', md: null }}
      >
        <FingerIcon label={tooltip} color={color} icon={icon} />

        <Box
          id={`${id}-label`}
          width={labelWidth}
          flexShrink={0}
          fontWeight={'medium'}
          color={'muted'}
          pt={2}
          pb={{ base: 0, md: 2 }}
          px={{ base: 0, md: 2, xl: 3 }}
        >
          {getLabel()}
        </Box>

        <Flex
          gridColumnStart={2}
          gridColumnEnd={2}
          flexGrow={1}
          px={{ base: 0, md: 2, xl: 4 }}
          pt={{ base: 1, md: 1 }}
          pb={{ base: 2, md: 1 }}
          flexWrap={'wrap'}
        >
          {getValue()}

          {canOpen ? getOpenButton() : null}

          {addFieldBtn ? (
            <Button size={'sm'} onClick={() => onEdit(addFieldBtn.action!, props.data)}>
              {addFieldBtn.label} <VisuallyHidden>{label}</VisuallyHidden>
            </Button>
          ) : null}
        </Flex>

        {editPage && state !== InfoState.Skipped ? (
          <Box gridColumnStart={3} gridRow={'span 2 / span 2'} gridRowStart={1} mt={1} ml={2}>
            <Button size={'sm'} variant={'ghost'} onClick={() => onEdit(editPage, props.data)}>
              {t('common:edit')} <VisuallyHidden>{label}</VisuallyHidden>
            </Button>
          </Box>
        ) : null}
      </Box>

      {canOpen ? (
        <Collapse in={isConflictOpen}>
          <Stack py={4}>
            {options.map((option: InfoOption, i: number) => {
              return (
                <Flex key={i}>
                  <Box
                    color={'muted'}
                    ml={4}
                    pl={{ base: 4, md: option.id === TransactionStatus.Pending ? 0 : 16 }}
                    py={1}
                    fontWeight={'medium'}
                    width={labelWidth}
                  >
                    {option.label}

                    {option.label === 'Passport' ? (
                      <Span color={'cyan.500'} _dark={{ color: 'cyan.400' }} ml={2}>
                        <FontAwesomeIcon icon={faCameraViewfinder} />
                      </Span>
                    ) : null}
                  </Box>

                  <Box
                    pl={{ base: 2, md: 10 }}
                    py={1}
                    flexGrow={{ base: 1, md: 0 }}
                    width={{ base: undefined, md: 48 }}
                  >
                    {getOptionValue(option)}
                  </Box>

                  {!option.disabled ? getOptionBtn(option) : null}

                  {option.editPage ? (
                    <Button
                      size={'sm'}
                      variant={'ghost'}
                      ml={'auto'}
                      onClick={() => onEdit(option.editPage!, props.data)}
                    >
                      {t('common:edit')} <VisuallyHidden>{label}</VisuallyHidden>
                    </Button>
                  ) : null}
                </Flex>
              );
            })}
          </Stack>
        </Collapse>
      ) : null}
    </Box>
  );
}
